import { m } from 'motion/react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import {
  fadeInUp,
  staggerContainer,
  staggerChild,
  defaultViewport,
  getReducedMotionVariants,
} from '@/lib/motion';
import { useLandingSectionTracking } from '@/hooks/useLandingSectionTracking';
import { useTranslation } from '@/i18n/i18n';

export const HowItWorks = () => {
  const shouldReduceMotion = useReducedMotion();
  const sectionRef = useLandingSectionTracking('how_it_works');
  const { t } = useTranslation();

  const steps = [
    {
      number: '1',
      title: t('howItWorks.step1.title'),
      description: t('howItWorks.step1.description'),
    },
    {
      number: '2',
      title: t('howItWorks.step2.title'),
      description: t('howItWorks.step2.description'),
    },
    {
      number: '3',
      title: t('howItWorks.step3.title'),
      description: t('howItWorks.step3.description'),
    },
    {
      number: '4',
      title: t('howItWorks.step4.title'),
      description: t('howItWorks.step4.description'),
    },
    {
      number: '5',
      title: t('howItWorks.step5.title'),
      description: t('howItWorks.step5.description'),
    },
  ];

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      aria-labelledby="how-it-works-heading"
      className="py-16 px-6 md:px-8 bg-card"
    >
      <div className="max-w-5xl mx-auto">
        {/* Section header */}
        <m.div
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={shouldReduceMotion ? getReducedMotionVariants(fadeInUp) : fadeInUp}
        >
          <h2
            id="how-it-works-heading"
            className="text-3xl md:text-5xl font-semibold mb-4 tracking-tight"
          >
            {t('howItWorks.heading')
              .split('<bold>')
              .map((part, i) => {
                if (i === 0) return part;
                const [bold, rest] = part.split('</bold>');
                return (
                  <span key={bold}>
                    <span className="font-extrabold">{bold}</span>
                    {rest}
                  </span>
                );
              })}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('howItWorks.subheading')}
          </p>
        </m.div>

        {/* Steps - horizontal on desktop */}
        <div className="relative">
          {/* Connection line for desktop */}
          <div className="hidden lg:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-primary/10 via-primary/40 to-primary/10" />

          <m.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={
              shouldReduceMotion ? getReducedMotionVariants(staggerContainer) : staggerContainer
            }
          >
            {steps.map((step) => (
              <m.div
                key={step.number}
                className="relative group text-center"
                variants={
                  shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild
                }
              >
                {/* Step number */}
                <p className="font-mono text-sm text-muted-foreground mb-4 relative z-10">
                  0{step.number}
                </p>

                {/* Step content */}
                <h3 className="text-base font-semibold mb-2 text-foreground">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
              </m.div>
            ))}
          </m.div>
        </div>
      </div>
    </section>
  );
};
